// external dependencies
import React from 'react'
import { string } from 'prop-types'
import styled from '@emotion/styled'

// components
import { AnchorTag } from '../AnchorTag'
import { Download } from '../svgs/Download'

// utils
import { makeImgResizerUrl } from '@jelly/utils'

const ArticleWrap = styled.div`
	border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	color: ${({ theme }) => theme.colors.text};
	display: flex;
	flex-direction: column;
	margin: 40px auto;
	padding-bottom: 2rem;

	${({ theme }) => theme.MQ.S} {
		flex-direction: row;
	}
`

const ImgBlock = styled.div`
	margin-bottom: 1rem;

	${({ theme }) => theme.MQ.S} {
		margin-left: 1rem;
	}
`

const ImgTitle = styled.div`
	font-size: 1rem;
	font-weight: 700;
	margin-bottom: 0.5rem;
`

const Img = styled.img`
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
	max-width: none;
	width: 100%;

	${({ theme }) => theme.MQ.S} {
		width: 16rem;
	}
`

const ContentWrap = styled.div`
	${({ theme }) => theme.MQ.S} {
		margin-left: 2rem;
	}
`

const HeadLine = styled.h4`
	font-size: 1.25rem;
	font-weight: 700;
	line-height: 1.25;
	margin-bottom: 1rem;
	margin-top: 0;
`

const Blurb = styled.div`
	font-size: 1rem;
	line-height: 1.5;
	margin-bottom: 0;
	margin-top: 1rem;
`

const GuideLink = styled(AnchorTag)`
	color: ${({ theme }) => theme.colors.orange};

	&:hover {
		color: ${({ theme }) => theme.colors.teal};
		cursor: pointer;
	}
`

const DownloadLink = styled(AnchorTag)`
	align-items: center;
	color: ${({ theme }) => theme.colors.contentLink};
	display: inline-flex;

	&:hover {
		cursor: pointer;
	}
`

const DownloadText = styled.span`
	padding-left: 5px;
`

const ImageAnchor = styled(AnchorTag)`
	&:hover {
		cursor: pointer;
	}
`

Guide.propTypes = {
	description: string,
	downloadText: string,
	guideLink: string,
	guideTitle: string,
	headline: string,
	imgPath: string,
}

/**
 * An individual "downloadable guide" for the SEO page. This card displays:
 * - a headline,
 * - a link to download the asset via a safe, non-tabnapping link
 * - a short blurb describing the asset
 * - a thumbnail preview of the guide
 * - a caption above the asset
 *
 * Additionally the layout is responsive with the thumbnail moving above the rest at the smallest breakpoint.
 * For the width of the image going into the image resizer I opted for 480 as that's the widest the image would be.
 */
export function Guide({ description, downloadText, guideLink, guideTitle, headline, imgPath }) {
	return (
		<ArticleWrap>
			<ImgBlock>
				<ImgTitle>{guideTitle}</ImgTitle>
				<ImageAnchor href={guideLink}>
					<Img
						className="lazyload"
						alt={guideTitle}
						data-src={makeImgResizerUrl({ src: imgPath, method: 'w', w: 480 })}
					/>
				</ImageAnchor>
			</ImgBlock>
			<ContentWrap>
				<GuideLink href={guideLink}>
					<HeadLine>{headline}</HeadLine>
				</GuideLink>
				<DownloadLink href={guideLink}>
					<Download fill="#0072d1" margin="0" width="14px" height="14px" />
					<DownloadText>{downloadText}</DownloadText>
				</DownloadLink>
				<Blurb dangerouslySetInnerHTML={{ __html: description }} />
			</ContentWrap>
		</ArticleWrap>
	)
}
